import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { BaseService } from '../base/base.service';
import { switchMap, catchError, tap, shareReplay } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AuthService extends BaseService {
    prefixPath = 'auth';
    //Used by to refresh user data
    resetUserSubject = new Subject<any>();
    private userRequest$: Observable<any> | null = null;

    private userSubject = new BehaviorSubject<any | null>(null);
    public user$ = this.userSubject.asObservable();
    private initialized = false;

    constructor(httpClient: HttpClient) {
      super(httpClient);
    }

    loginWithCsrf(data): Observable<any> {
      return this.httpGet('sanctum/csrf-cookie', {  }, true).pipe(
        switchMap(() =>
          this.httpPost(`${this.prefixPath}/login`, data, { withCredentials: true })
        ),
        switchMap(() =>
          this.httpGet(`${this.prefixPath}/get-user`, { withCredentials: true }).pipe(
            tap(user => {
              this.initialized = true;
              this.userSubject.next(user);
            }),
            catchError(() => {
              this.initialized = true;
              this.userSubject.next(null);
              return of(null);
            })
          )
        )
      );
    }

    login(data): Observable<any> {
      return this.httpPost(`${this.prefixPath}/login`, data)
    }

    generateToken(data): Observable<any> {
      return this.httpPost(`${this.prefixPath}/generate-token`, data)
    }

    register(data): Observable<any> {
      return this.httpGet('sanctum/csrf-cookie', { withCredentials: true }, true).pipe(
        switchMap(() =>
          this.httpPost(`${this.prefixPath}/register`, data, { withCredentials: true })
        ),
        switchMap(() =>
          this.httpPost(`${this.prefixPath}/login`, {
            email: data.email,
            password: data.password
          }, { withCredentials: true })
        ),
        switchMap(() =>
          this.httpGet(`${this.prefixPath}/get-user`, { withCredentials: true }).pipe(
            tap(user => {
              this.initialized = true;
              this.userSubject.next(user);
            }),
            catchError(() => {
              this.initialized = true;
              this.userSubject.next(null);
              return of(null);
            })
          )
        )
      );
    }

    logout(): Observable<any> {
      return this.httpPost(`${this.prefixPath}/logout`, {}, { withCredentials: true }).pipe(
        tap(() => {
          this.userSubject.next(null);
          this.initialized = false;
        })
      );
    }

    getUser(): Observable<any | null> {
      if (this.initialized) {
        return this.user$;
      }
    
      if (!this.userRequest$) {
        this.userRequest$ = this.httpGet(`${this.prefixPath}/get-user`, { withCredentials: true }).pipe(
          tap(user => {
            this.userSubject.next(user);
            this.initialized = true;
          }),
          catchError(() => {
            this.userSubject.next(null);
            this.initialized = true;
            return of(null);
          }),
          tap(() => {
            this.userRequest$ = null; // reset after completion
          }),
          shareReplay(1)
        );
      }
    
      return this.userRequest$;
    }

    getCurrentUser(): any | null {
      return this.userSubject.getValue();
    }

    existEmail(email): Observable<any> {
      return this.httpPost(`${this.prefixPath}/exist-email`, { email })
    }

    forgotPassword(email): Observable<any> {
      return this.httpPost(`${this.prefixPath}/forgot`, { email })
    }

    resetPassword(data): Observable<any> {
      return this.httpPost(`${this.prefixPath}/reset`, data)
    }

    changePassword(data): Observable<any> {
      return this.httpPost(`${this.prefixPath}/change-password`, data)
    }
}